import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {addListener} from '../../../store/statusChecker';
import {setChatConnection, setDeployTask, TASK_STATUS, useDeploy} from '../useDeploy';
import {useDeployTask} from './useDeployTask';
import {TaskInfo} from '../../../types/metadata/TaskInfo';
import {ChatConnection, ChatConnectionStatus, MessageType} from '../../Chat/Chat';
import {TestInitiateRequest} from '../../../api/test/TestInitiate';
import {toast} from '../../../base-components/StudioToast';
import Util from '../../../util';

class WebsocketChatConnection implements ChatConnection {
  private consumeText: (m: string, t: MessageType) => void = () => {};
  private consumeImage: (m: string) => void = () => {};
  private consumeStatus: (s: ChatConnectionStatus) => void = () => {};
  private websocket: WebSocket;
  private timer: NodeJS.Timeout;
  private connectionStatus: ChatConnectionStatus = ChatConnectionStatus.CONNECTING;
  private onRegister: (chatConnection: WebsocketChatConnection, message: string) => void;

  constructor(
    url: string,
    onRegister: (chatConnection: WebsocketChatConnection, message: string) => void
  ) {
    this.websocket = new WebSocket(url);
    this.websocket.onmessage = event => this.onMessage(event);
    this.websocket.onerror = event => this.onError(event);
    this.websocket.onopen = () =>
      this.websocket.send(JSON.stringify({id: 'registerDataChannel'}));
    this.timer = setInterval(() => {
      this.websocket.send(
        JSON.stringify({
          id: 'heartbeat',
        })
      );
    }, 10_000);
    this.onRegister = onRegister;
  }

  sendText(payload: string): void {
    this.send('text', payload);
  }

  sendImage(payload: string): void {
    this.send('image', payload);
  }

  send(type: string, payload: string): void {
    if (this.isChatCommunicationOpen()) {
      const data = JSON.stringify({
        id: 'data',
        data: {type: type, payload: payload},
      });
      this.websocket.send(data);
    } else {
      toast.error(`Can't send in status ${this.connectionStatus}`);
    }
  }

  setTextConsumer(x: (m: string, t: MessageType) => void): void {
    this.consumeText = x;
  }

  setImageConsumer(x: (m: string) => void): void {
    this.consumeImage = x;
  }

  setStatusConsumer(x: (s: ChatConnectionStatus) => void): void {
    this.consumeStatus = x;
  }

  updateTaskInfo(x: TaskInfo): void {
    if (
      this.connectionStatus === ChatConnectionStatus.DISCONNECTED ||
      this.connectionStatus === ChatConnectionStatus.ERROR
    ) {
      return;
    }
    if (Util.isRealNumberEqual(x.percentCompleted, 100)) {
      this.setStatus(ChatConnectionStatus.DISCONNECTED);
      x.warnings.forEach(w => {
        this.consumeText(`${w.message}\n${w.detailMessage}`, 'SYSTEM');
      });
      x.errors.forEach(w => {
        this.consumeText(`${w.message}\n${w.detailMessage}`, 'SYSTEM');
        this.setStatus(ChatConnectionStatus.ERROR);
      });
      this.close();
    }
  }

  close(): void {
    clearInterval(this.timer);
    if (this.isWebsocketOpen()) {
      this.websocket.send(JSON.stringify({id: 'disconnect'}));
    }
    this.websocket.close();
    this.consumeText('Disconnected', 'SYSTEM');
  }

  private isChatCommunicationOpen() {
    return this.connectionStatus === ChatConnectionStatus.ACTIVE;
  }

  private isWebsocketOpen() {
    return this.websocket.readyState === WebSocket.OPEN;
  }

  private setStatus(s: ChatConnectionStatus) {
    if (this.connectionStatus !== s) {
      this.consumeStatus(s);
      this.connectionStatus = s;
    }
  }

  private onMessage(event: MessageEvent) {
    const msg = JSON.parse(event.data);
    if (msg.id === 'acknowledgeDataChannel') {
      this.setStatus(ChatConnectionStatus.CONNECTED);
      this.onRegister(this, msg.sessionId);
      this.consumeText('Connected. Waiting for the application to start...', 'SYSTEM');
      return;
    }
    if (msg.id === 'startCommunication') {
      this.consumeText('Application started!', 'SYSTEM');
      this.setStatus(ChatConnectionStatus.ACTIVE);
      return;
    }
    if (msg.id === 'stopCommunication') {
      this.setStatus(ChatConnectionStatus.DISCONNECTED);
      this.close();
      return;
    }
    if (msg.id === 'data') {
      if (msg.data.type === 'text') {
        this.consumeText(msg.data.payload, 'APPLICATION');
      } else if (msg.data.type === 'image') {
        this.consumeImage(msg.data.payload);
      } else if (msg.data.type === 'error') {
        this.consumeText('ERROR: ' + msg.data.payload, 'APPLICATION');
      } else {
        this.consumeText('Protocol error: unknown type', 'SYSTEM');
        toast.error(`Unexpected type ${event.data}`);
        this.setStatus(ChatConnectionStatus.ERROR);
        this.close();
      }
      return;
    }
    this.consumeText('Protocol error: unknown id', 'SYSTEM');
    toast.error(`Unexpected id ${event.data}`);
    this.setStatus(ChatConnectionStatus.ERROR);
    this.close();
  }

  private onError(event: Event) {
    console.error(event);
    this.consumeText('Connection error', 'SYSTEM');
    this.setStatus(ChatConnectionStatus.ERROR);
  }
}

export const useTextTask = (projectId: string) => {
  const reduxDispatch = useDispatch();
  const [{chatConnection}, dispatch] = useDeploy();
  const {startDeploy, terminateDeploy} = useDeployTask(projectId);

  const monitorStatus = useCallback(
    (deploymentId, onMonitoringResponse) => {
      reduxDispatch(
        addListener('GSP_DEPLOY', (status: TaskInfo) => {
          if (status.deploymentId !== deploymentId) {
            return;
          }
          dispatch(setDeployTask({monitoringResponse: status, deploymentId}));
          onMonitoringResponse?.(status);
        })
      );
    },
    [dispatch, reduxDispatch]
  );

  const start = ({
    projectId,
    applicationId,
    schedulerId,
    name,
    description,
    signalingServerUrl,
  }: {
    projectId: string;
    applicationId: string;
    schedulerId?: string;
    name: string;
    description: string;
    signalingServerUrl: string;
  }) => {
    try {
      const websocketChatConnection = new WebsocketChatConnection(
        signalingServerUrl,
        async (chatConnection, streamingSessionId) => {
          try {
            const config = {
              textInferenceRequest: {
                name,
                description,
                projectId,
                applicationId,
                schedulerId,
                streamingSessionId,
              },
            } as TestInitiateRequest;
            dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS}));

            const deploymentId = await startDeploy(config);
            dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS, deploymentId}));
            monitorStatus(deploymentId, (taskInformation: TaskInfo) =>
              chatConnection.updateTaskInfo(taskInformation)
            );
          } catch (error) {
            const errorMessage = (error as Error).message;

            toast.error(errorMessage);
            terminateDeploy();
            dispatch(setChatConnection(undefined));
          }
        }
      );

      dispatch(setChatConnection(websocketChatConnection));
    } catch (error) {
      const errorMessage = (error as Error).message;

      toast.error(errorMessage);
    }
  };

  const stop = () => {
    terminateDeploy();
    chatConnection?.close();
    dispatch(setChatConnection(undefined));
  };

  return [start, stop] as const;
};
